/**
 * CAD Inversify 容器工厂 —— 创建桥接 EngineContext 的 CAD IoC 容器
 */

import { Container } from 'inversify'
import { CADModule } from '@/plugins/cad/module/CADModule/CADModule'
import type { CadRenderInvalidator } from '@/plugins/cad/module/CADModule/renderInvalidation'
import type { EngineContext } from '@/runtime/EngineContext'
import { TYPES } from './types'

/**
 * 创建桥接 EngineContext 的 CAD Inversify 容器。
 *
 * @param ctx - 当前引擎上下文，用于注入 Scene/Camera/Renderer 等服务
 * @returns 已加载 CADModule 的 Inversify 容器
 */
export function createCadContainer(ctx: EngineContext): Container {
  const cadContainer = new Container()

  // 桥接 EngineContext 到 CAD IoC
  cadContainer.bind(TYPES.EngineContext).toConstantValue(ctx)
  cadContainer.bind(TYPES.Scene).toDynamicValue(() => ctx.sceneGraph.mainScene)
  cadContainer.bind(TYPES.SelectionService).toDynamicValue(() => ctx.getService('SelectionService'))
  cadContainer.bind(TYPES.PickingService).toDynamicValue(() => ctx.getService('PickingService'))
  cadContainer
    .bind(TYPES.UnitPresentationService)
    .toConstantValue(ctx.getService('UnitPresentationService'))
  cadContainer.bind<CadRenderInvalidator>(TYPES.RenderInvalidator).toConstantValue({
    invalidate(reason: string) {
      if (ctx.hasService('RenderInvalidationService')) {
        ctx.getService('RenderInvalidationService').invalidate(reason)
      }
    },
  })

  // camera/renderer 引用每帧更新，使用 toDynamicValue 保证获取最新值
  cadContainer.bind(TYPES.Camera).toDynamicValue(() => {
    const vc = ctx.getService('ViewportCamera')
    return vc.getActiveCamera()
  })
  cadContainer.bind(TYPES.Renderer).toDynamicValue(() => ctx.renderer)

  cadContainer.load(CADModule)
  return cadContainer
}
